import { ColorTypes } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import { useRecoilValue } from 'recoil';

import { productsCartState } from '../../../../../../../store/batchTicket/atom';
import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';

import { getValidationCreateSchema } from './constants';
import { ClubsFormData } from './types';

export const useClubsAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['clubs'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const [isFocused, setIsFocused] = useState(false);
  const [prevValue, setPrevValue] = useState(0);
  const [showMessageAdjustUnitPrice, setShowMessageAdjustUnitPrice] = useState(false);

  const clubsOffersSelected = useRecoilValue(productsCartState).clubs;

  const valueAppliedBasket = clubsOffersSelected.items.findIndex(
    (item) => item.clubId === offer.clubId
  );

  const [valueApplied, setValueApplied] = useState(
    clubsOffersSelected?.items[valueAppliedBasket]?.orderValue ||
      offer.orderValue ||
      offer.minimumApplication
  );

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(offer.minimumApplication);

  const tableForm = useForm<ClubsFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      createAppliedAmount: valueAppliedFormatted,
    },
    resolver: zodResolver(
      getValidationCreateSchema(offer.minimumApplication || 0, Number.MAX_SAFE_INTEGER)
    ),
  });

  const isAmountWithinRange = (amount: number) => {
    return (
      offer.minimumApplication &&
      Number.MAX_SAFE_INTEGER &&
      prevValue !== amount &&
      amount >= offer.minimumApplication &&
      amount <= Number.MAX_SAFE_INTEGER
    );
  };

  const isMultipleOf = (value: number, multiple: number): boolean => {
    return value % multiple === 0;
  };

  const getMultipleValue = (value: number, multiple: number): number => {
    const quotient = Math.floor(value / multiple);
    return quotient * multiple;
  };

  const updateAppliedAmount = async (amount: number, isMultiple: boolean) => {
    const updateValues = (newAmount: number) => {
      const newFormattedAmount = Utils.Mask.Money(newAmount);
      tableForm.setValue('createAppliedAmount', newFormattedAmount);
      setValueApplied(newAmount);
      setPrevValue(newAmount);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount });
      if (response) {
        updateValues(amount);
      } else {
        updateValues(prevValue);
      }
    } else {
      updateValues(amount);
    }

    if (!isMultiple) {
      setShowMessageAdjustUnitPrice(true);
    }
  };

  const processInputAmount = (unformattedAmount: number) => {
    const unitPrice = Number((offer.minimumMovement || 0).toFixed(2));
    const isMultiple = isMultipleOf(unformattedAmount, unitPrice);
    const adjustedAmount = getMultipleValue(unformattedAmount, unitPrice);

    if (isAmountWithinRange(unformattedAmount) && prevValue !== unformattedAmount) {
      updateAppliedAmount(adjustedAmount, isMultiple);
    }
  };

  const handleInputBlur = () => {
    const currentValue = tableForm.getValues('createAppliedAmount');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    if (currentValue !== minValueFormatted) {
      processInputAmount(currentValueUnformatted);
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setShowMessageAdjustUnitPrice(false);
    setPrevValue(offer?.minimumApplication || 0);

    if (tableForm.formState.errors?.createAppliedAmount) {
      tableForm.resetField('createAppliedAmount');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('createAppliedAmount');
    tableForm.clearErrors('createAppliedAmount');
  };

  const calculateFinalAmount = (amount: number, unitPrice: number) => {
    if (isMultipleOf(amount, unitPrice)) {
      return amount;
    }
    return getMultipleValue(amount, unitPrice);
  };

  const handleSubmit = async ({ createAppliedAmount }: ClubsFormData) => {
    const unformattedAmount = Utils.Unmask.Money(createAppliedAmount);
    const unitPrice = Number((offer.minimumMovement || 0).toFixed(2));
    const finalAmount = calculateFinalAmount(unformattedAmount, unitPrice);

    const updateFormValues = (amount: number) => {
      tableForm.setValue('createAppliedAmount', Utils.Mask.Money(amount));
      setValueApplied(amount);
    };

    const shouldShowAdjustUnitPriceMessage = () => {
      return !isMultipleOf(unformattedAmount, unitPrice);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount: finalAmount });

      if (response) {
        updateFormValues(finalAmount);

        if (shouldShowAdjustUnitPriceMessage()) {
          setShowMessageAdjustUnitPrice(true);
        }
      } else {
        updateFormValues(prevValue);
      }
    } else {
      updateFormValues(finalAmount);

      if (shouldShowAdjustUnitPriceMessage()) {
        setShowMessageAdjustUnitPrice(true);
      }
    }

    setIsFocused(false);
  };

  const helpTextWhenIsFocused = showMessageAdjustUnitPrice
    ? 'Ajustado por preço unitário'
    : `Mínimo: ${offer.minimumMovementNormalized}`;

  const messageAdjustUnitPriceColor = {
    color: (isFocused ? 'brand.tertiary.principal' : 'neutral.light') as ColorTypes.Option,
  };

  const productsDetailText = 'Veja mais detalhes\nsobre este produto';

  return {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    showMessageAdjustUnitPrice,
    helpTextProps: messageAdjustUnitPriceColor,
    helpText: helpTextWhenIsFocused,
    hasError: tableForm.formState.errors?.createAppliedAmount,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    productsDetailText,
  };
};

export type ClubsAssetController = ReturnType<typeof useClubsAssetController>;

export const useClubsDetailsController = (_offer: AssetDefaultOrSelectedTypeMap['clubs']) => {
  const channelOptions = useMemo(
    () => [
      { label: 'Telefone', value: 'Telefone' },
      { label: 'Email', value: 'Email' },
    ],
    []
  );

  const [hasError, setHasError] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [observation, setObservation] = useState('');
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(channelOptions[0].value);

  const handleClearDate = () => {
    setSelectedDate(undefined);
  };

  const handleSetDate = (date: Date) => {
    setSelectedDate(date);
  };

  const handleSetObservation = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setObservation(e.nativeEvent.text);
  };

  const handleSetAgreement = (selected: boolean) => {
    setIsAgreementChecked(selected);
  };

  const handleSetSelectedChannel = (channel: string) => {
    setSelectedChannel(channel);
  };

  useEffect(() => {
    if (!selectedDate || !isAgreementChecked) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  }, [isAgreementChecked, selectedDate]);

  return {
    selectedDate,
    handleSetDate,
    handleClearDate,
    hasError,
    observation,
    handleSetObservation,
    isAgreementChecked,
    handleSetAgreement,
    channelOptions,
    selectedChannel,
    handleSetSelectedChannel,
  };
};

export type ClubsDetailsController = ReturnType<typeof useClubsDetailsController>;
